import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { PresetsTab } from '@live/pages/live-ai-models-and-presets-page/interfaces';
import { AffectedCamerasStatus } from '@live/pages/live-ai-models-and-presets-page/tabs/live-presets-tab/components/apply-preset/interfaces';
import * as PresetActions from '@live/actions/live-presets.actions';
import { IPresetError } from '@live/pages/live-ai-models-and-presets-page/live-preset-errors.class';

import IPreset = Microsoft.VideoIndexer.Live.Contracts.IPreset;
import INextPage = Microsoft.VideoIndexer.Contracts.INextPage;

//TODO: those interfaces will be used in more than one place, so it should be moved to a shared location
export interface Preset extends IPreset {
  isLoaded?: boolean;
  affectedCamerasCount?: number;
  affectedCamerasStatus?: AffectedCamerasStatus;
}

export interface IState extends EntityState<Preset> {
  loaded: boolean;
  isSaving: boolean;
  error: IPresetError;
  nextPage: INextPage;
  page: {
    selectedTab: PresetsTab;
  };
}

export function selectPresetId(preset: Preset): string {
  return preset.id;
}

export const adapter: EntityAdapter<Preset> = createEntityAdapter<Preset>({
  selectId: selectPresetId
});

export const initialState: IState = adapter.getInitialState({
  loaded: false,
  isSaving: false,
  error: undefined,
  nextPage: {
    pageSize: 25,
    skip: 0,
    done: false,
    totalCount: null
  },
  page: {
    selectedTab: PresetsTab.Presets
  }
});

const livePresetsReducer = createReducer(
  initialState,
  on(PresetActions.createPresetSuccess, (state, { preset }) => {
    return adapter.upsertOne({ ...preset, isLoaded: true, affectedCamerasCount: 0, affectedCamerasStatus: AffectedCamerasStatus.LOADED }, state);
  }),
  on(PresetActions.updatePresetSuccess, PresetActions.getPresetSuccess, (state, { preset }) => {
    return adapter.upsertOne({ ...preset, isLoaded: true }, state);
  }),
  on(PresetActions.getPreset, (state, { presetId }) => {
    return adapter.updateOne(
      { id: presetId, changes: { isLoaded: false, affectedCamerasCount: undefined, affectedCamerasStatus: AffectedCamerasStatus.NOT_LOADED } },
      state
    );
  }),
  on(PresetActions.getPresetFailed, (state, { error }) => {
    return { ...state, error };
  }),
  on(PresetActions.loadPresets, PresetActions.loadPresetsWithAffectedCamerasCount, (state, {}) => {
    return adapter.removeAll({ ...initialState });
  }),
  on(PresetActions.loadPresetsSuccess, (state, { presets, nextPage }) => {
    const loadedPresets = presets.map(preset => ({ ...preset, isLoaded: true }));
    return adapter.addMany(loadedPresets, {
      ...state,
      error: undefined,
      loaded: true,
      nextPage
    });
  }),
  on(PresetActions.loadPresetsFailed, (state, { error }) => {
    return adapter.removeAll({ ...state, loaded: true, error });
  }),
  on(PresetActions.presetTabSelected, (state, { tab }) => {
    return { ...state, page: { ...state.page, selectedTab: tab } };
  }),
  on(PresetActions.getAffectedCamerasCountFailed, (state, { presetId }) => {
    return adapter.updateOne(
      { id: presetId, changes: { affectedCamerasCount: undefined, affectedCamerasStatus: AffectedCamerasStatus.ERROR } },
      state
    );
  }),
  on(PresetActions.deletePresetSuccess, (state, { presetId }) => {
    return adapter.removeOne(presetId, state);
  }),
  on(PresetActions.applyPreset, PresetActions.createPreset, PresetActions.updatePreset, state => {
    return { ...state, isSaving: true };
  }),
  on(PresetActions.applyPresetSuccess, PresetActions.createPresetSuccess, PresetActions.updatePresetSuccess, state => {
    return { ...state, isSaving: false };
  }),
  on(PresetActions.applyPresetFailed, PresetActions.createPresetFailed, PresetActions.updatePresetFailed, (state, { error }) => {
    return { ...state, isSaving: false, error };
  })
);

export function reducer(state: IState | undefined, action: Action) {
  return livePresetsReducer(state, action);
}
